import React from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { isEmpty } from "lodash";
import { TAnalyticsFilters } from "../../shared/types/analyticsFilters.type";

type FilterKeys = keyof TAnalyticsFilters;

// Keys resolved by id -> name through `filters`
const LIST_KEYS = [
  "horses",
  "riders",
  "track_conditions",
  "track_names",
  "track_surfaces",
  "trainers",
  "training_types",
  "horse_shoeings",
] as const satisfies readonly FilterKeys[];

// Keys whose input values are displayed directly (no lookup)
const VALUE_KEYS = [
  "hands",
  "horse_ages",
] as const satisfies readonly FilterKeys[];

const ALL_KEYS = [...LIST_KEYS, ...VALUE_KEYS] as const;

export type ListKey = (typeof LIST_KEYS)[number];
export type ValueKey = (typeof VALUE_KEYS)[number];
type DisplayKey = ListKey | ValueKey;

const FILTER_LABELS: Record<DisplayKey, string> = {
  horses: "Horses",
  riders: "Riders",
  track_conditions: "Track conditions",
  track_names: "Tracks",
  track_surfaces: "Surfaces",
  trainers: "Trainers",
  training_types: "Training types",
  horse_shoeings: "Shoeings",
  hands: "Hands",
  horse_ages: "Horse ages",
};

const HAND_LABELS: Record<number, string> = {
  [-1]: "Left hand",
  1: "Right hand",
  0: "Straight line",
};

const VALUE_FORMATTERS: Record<ValueKey, (v: number) => string> = {
  hands: (v) => HAND_LABELS[v] ?? String(v),
  horse_ages: (v) => `${v} yr${v === 1 ? "" : "s"}`,
};

const MAX_VISIBLE = 3;

export type FilterItem = NonNullable<TAnalyticsFilters[ListKey]>[number] & {
  id: number;
  name: string;
};
type FilterResult = { names: string[]; deleted: number };
type BuiltFilters = Partial<Record<DisplayKey, FilterResult>>;
type Inputs = Partial<Record<DisplayKey, number[]>>;

type RenderFiltersProps = {
  filters?: TAnalyticsFilters;
  inputs: any;
};

const chipSx = { height: 20, fontSize: 12, "& .MuiChip-label": { px: 0.75 } };

function RenderFilters({ filters, inputs }: RenderFiltersProps) {
  const buildData = (): BuiltFilters => {
    return ALL_KEYS.reduce<BuiltFilters>((acc, key) => {
      const ids = inputs[key];
      if (isEmpty(ids)) return acc;

      if ((VALUE_KEYS as readonly string[]).includes(key)) {
        const format = VALUE_FORMATTERS[key as ValueKey];
        acc[key] = { names: ids.map(format), deleted: 0 };
        return acc;
      }

      if (!filters || isEmpty(filters)) return acc;

      const items = (filters[key as ListKey] ?? []) as unknown as FilterItem[];
      const names = ids
        .map((id:number) => items.find((f) => f.id === id)?.name)
        .filter((n:any): n is string => n !== undefined);

      acc[key] = { names, deleted: ids.length - names.length };
      return acc;
    }, {});
  };

  const entries = Object.entries(buildData()) as [DisplayKey, FilterResult][];

  if (entries.length === 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
        <FilterListOffIcon sx={{ fontSize: 16, color: "text.disabled" }} />
        <Typography variant="caption" color="text.secondary">
          No filters applied
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        columnGap: 3,
        rowGap: 0.75,
        py: 0.5,
      }}
    >
      {entries.map(([key, { names, deleted }]) => {
        const visible = names.slice(0, MAX_VISIBLE);
        const hidden = names.slice(MAX_VISIBLE);

        return (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ width: 96, flexShrink: 0, fontWeight: 600 }}
            >
              {FILTER_LABELS[key]}
            </Typography>

            <Stack
              direction="row"
              gap={0.5}
              flexWrap="wrap"
              sx={{ minWidth: 0 }}
            >
              {visible.map((name, i) => (
                <Chip
                  key={`${name}-${i}`}
                  label={name}
                  size="small"
                  variant="outlined"
                  sx={chipSx}
                />
              ))}

              {hidden.length > 0 && (
                <Tooltip title={hidden.join(", ")}>
                  <Chip label={`+${hidden.length}`} size="small" sx={chipSx} />
                </Tooltip>
              )}

              {deleted > 0 && (
                <Chip
                  label={`${deleted} deleted`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={chipSx}
                />
              )}
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
}

export default RenderFilters;
